/**
 * Global Loading Service
 * Manages global loading state for API calls, route changes, and async operations
 */

export type LoaderType = "central" | "inline";

type LoadingCallback = (isLoading: boolean, loaderType?: LoaderType) => void;
type InlineLoadingCallback = (elementId: string | null, isLoading: boolean) => void;

class LoadingService {
  private loadingCount = 0;
  private callbacks: Set<LoadingCallback> = new Set();
  private inlineCallbacks: Set<InlineLoadingCallback> = new Set();
  private isLoading = false;
  private currentLoaderType: LoaderType = "central";
  private inlineLoadingElement: string | null = null;
  private showTime: number | null = null;
  private minLoadingTime = 500; // Minimum time loader should be visible (ms)
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Subscribe to loading state changes
   */
  subscribe(callback: LoadingCallback): () => void {
    this.callbacks.add(callback);
    // Immediately notify with current state
    callback(this.isLoading, this.currentLoaderType);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Subscribe to inline loading state changes
   */
  subscribeInline(callback: InlineLoadingCallback): () => void {
    this.inlineCallbacks.add(callback);
    // Immediately notify with current state
    callback(this.inlineLoadingElement, this.inlineLoadingElement !== null);
    return () => {
      this.inlineCallbacks.delete(callback);
    };
  }

  /**
   * Show loading
   * @param loaderType - "central" for full-screen overlay, "inline" for button-level loading
   * @param elementId - Optional element ID for inline loading (e.g., button ID)
   */
  show(loaderType: LoaderType = "central", elementId?: string): void {
    this.loadingCount++;
    console.log(
      "LoadingService: show() called, loadingCount:",
      this.loadingCount,
      "type:",
      loaderType
    );

    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    // Track when loading was shown
    if (!this.isLoading) {
      this.currentLoaderType = loaderType;
      this.showTime = Date.now();

      // IMPORTANT: even for inline loading we must set isLoading=true,
      // otherwise hide() will never run its completion path.
      this.updateLoadingState(true, loaderType);

      if (loaderType === "inline" && elementId) {
        this.inlineLoadingElement = elementId;
        this.updateInlineLoadingState(elementId, true);
      } else {
        this.inlineLoadingElement = null;
      }
    }
  }

  /**
   * Hide loading (with minimum display time)
   */
  hide(): void {
    this.loadingCount = Math.max(0, this.loadingCount - 1);

    if (this.loadingCount === 0 && this.isLoading) {
      const elapsed = this.showTime ? Date.now() - this.showTime : 0;
      const remainingTime = Math.max(0, this.minLoadingTime - elapsed);

      if (remainingTime > 0) {
        // Wait for minimum time before hiding
        this.hideTimeout = setTimeout(() => {
          if (this.currentLoaderType === "inline" && this.inlineLoadingElement) {
            this.updateInlineLoadingState(this.inlineLoadingElement, false);
            this.inlineLoadingElement = null;
            this.updateLoadingState(false, "inline");
          } else {
            this.updateLoadingState(false, "central");
          }
          this.showTime = null;
          this.hideTimeout = null;
        }, remainingTime);
      } else {
        // Already shown for minimum time, hide immediately
        if (this.currentLoaderType === "inline" && this.inlineLoadingElement) {
          this.updateInlineLoadingState(this.inlineLoadingElement, false);
          this.inlineLoadingElement = null;
          this.updateLoadingState(false, "inline");
        } else {
          this.updateLoadingState(false, "central");
        }
        this.showTime = null;
      }
    }
  }

  /**
   * Reset loading state (force hide)
   */
  reset(): void {
    this.loadingCount = 0;
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    if (this.inlineLoadingElement) {
      this.updateInlineLoadingState(this.inlineLoadingElement, false);
      this.inlineLoadingElement = null;
    }
    // Force reset global state (overlay still controlled by loaderType in provider)
    this.updateLoadingState(false, "central");
    this.showTime = null;
  }

  /**
   * Get current loading state
   */
  getLoadingState(): boolean {
    return this.isLoading;
  }

  /**
   * Get current inline loading element ID
   */
  getInlineLoadingElement(): string | null {
    return this.inlineLoadingElement;
  }

  /**
   * Update loading state and notify subscribers
   */
  private updateLoadingState(newState: boolean, loaderType: LoaderType): void {
    if (this.isLoading !== newState || this.currentLoaderType !== loaderType) {
      this.isLoading = newState;
      this.currentLoaderType = loaderType;
      console.log(
        "LoadingService: Notifying subscribers, isLoading:",
        this.isLoading,
        "type:",
        loaderType
      );
      this.callbacks.forEach((callback) => callback(this.isLoading, loaderType));
    }
  }

  /**
   * Update inline loading state and notify subscribers
   */
  private updateInlineLoadingState(elementId: string, isLoading: boolean): void {
    this.inlineLoadingElement = isLoading ? elementId : null;
    console.log(
      "LoadingService: Notifying inline subscribers, elementId:",
      elementId,
      "isLoading:",
      isLoading
    );
    this.inlineCallbacks.forEach((callback) => callback(this.inlineLoadingElement, isLoading));
  }
}

export const loadingService = new LoadingService();
